import {utils} from '@wallaby-ui/core';
import {clone, isEmpty} from 'lodash';

import Filter from './filters/filter';
import {LIBRARY_NAME} from 'src/js/common/constants';
import {DynamicFilter} from 'src/js/lib/filtering/filters_definitions';

const TOKEN_AND = ' AND ';
const TOKEN_NOT = ' NOT ';
const TOKEN_OR = ' OR ';
const TOKEN_L_PAR = '(';
const TOKEN_R_PAR = ')';

const logger = utils.LogUtils.getLogger('FilterParser', LIBRARY_NAME);

class FilterParser {
  static getFilterLiterals(serializedFilter) {
    // TODO: it only accepts boolean expressions when operands are enclosed in parenthesis
    // from left to right, i.e.: (((A & B) & C) & D)
    // different enclosing is not accepted yet (e.g. (A & (B & (C & D))), or ((A & B) & (C & D))
    const deserializedFilter = [];
    const stack = [];
    if (!isEmpty(serializedFilter)) {
      stack.push(serializedFilter);
    }
    while (stack.length > 0) {
      let stackTop = stack.pop();
      if (
        stackTop[0] === TOKEN_L_PAR &&
        stackTop[stackTop.length - 1] === TOKEN_R_PAR
      ) {
        // remove bounding parenthesis
        stackTop = stackTop.slice(1, -1);
      }
      const orIdx = stackTop.lastIndexOf(TOKEN_OR);
      const andIdx = stackTop.lastIndexOf(TOKEN_AND);
      const notIdx = stackTop.lastIndexOf(TOKEN_NOT);

      const opIdx = Math.max(orIdx, andIdx, notIdx);
      let opLen = 0;
      let isUnary = false;
      if (opIdx === orIdx) {
        // operator OR
        opLen = TOKEN_OR.length;
      } else if (opIdx === andIdx) {
        // operator AND
        opLen = TOKEN_AND.length;
      } else if (opIdx === notIdx) {
        // operator NOT
        opLen = TOKEN_NOT.length;
        isUnary = true;
      }
      if (opIdx > -1) {
        if (isUnary) {
          // push (right) operand to the stack
          stack.push(stackTop.slice(opIdx + opLen));
        } else {
          // push right and left operand to the stack
          stack.push(stackTop.slice(opIdx + opLen));
          stack.push(stackTop.slice(0, opIdx));
        }
      } else {
        // add literal filter condition to the result
        deserializedFilter.push(stackTop);
      }
    }
    return deserializedFilter;
  }

  static instantiateFilterFromStringValue(filterSchema, property, stringValue) {
    const node = filterSchema.getFilterByName(property);
    if (node) {
      const type = node.getType();
      const FilterClass = DynamicFilter.getFilterClass(type);
      return FilterClass.fromString(stringValue);
    }
    logger.warn(
      `Could not find filter ${property} in schema: ${filterSchema.getFilters()}`
    );
    return null;
  }

  static instantiateFilterWithValue(filterSchema, property, value) {
    const node = filterSchema.getFilterByName(property);
    const type = node.getType();
    const FilterClass = DynamicFilter.getFilterClass(type);
    return new FilterClass({type, property, value});
  }

  static instantiateFilterFromSchema(node, type, parentName) {
    const FilterClass = DynamicFilter.getFilterClass(type);
    return FilterClass.fromSchema(node, parentName);
  }

  static fromString(serializedFilter, filterSchema) {
    let deserializedFilter = [];
    if (filterSchema.isLoaded()) {
      const filterLiterals = FilterParser.getFilterLiterals(serializedFilter);

      deserializedFilter = [];
      filterLiterals.forEach((filterLiteral) => {
        const sepIdx = filterLiteral.indexOf(':');
        const property = filterLiteral.slice(0, sepIdx);
        const parsedFilter = FilterParser.instantiateFilterFromStringValue(
          filterSchema,
          property,
          filterLiteral
        );
        if (parsedFilter) {
          deserializedFilter.push(parsedFilter);
        }
      });
    }

    return deserializedFilter;
  }

  static fromStringAsMapOfLiterals(serializedFilter) {
    const deserializedFilter = [];
    const filterLiterals = FilterParser.getFilterLiterals(serializedFilter);

    filterLiterals.forEach((filterLiteral) => {
      const sepIdx = filterLiteral.indexOf(':');
      const property = filterLiteral.slice(0, sepIdx);
      deserializedFilter[property] = filterLiteral;
    });

    return deserializedFilter;
  }

  static appendFilterLiteralToSerializedFilter(
    serializedFilter,
    filterLiteral
  ) {
    return serializedFilter + TOKEN_AND + filterLiteral;
  }

  static toString(selectedFilters) {
    // TODO: it encloses operands in parenthesis from left to right, i.e.: (((A & B) & C) & D)
    // TODO: filter literals are joined with the operator OR,
    // different operators are not supported yet
    let serializedFilter = '';

    const stack = FilterParser.toStringArray(selectedFilters);

    while (stack.length > 1) {
      const first = stack.pop();
      const second = stack.pop();
      const operator = TOKEN_AND;
      // stack.push(`${TOKEN_L_PAR}${first}${operator}${second}${TOKEN_R_PAR}`);
      stack.push(`${first}${operator}${second}`);
    }
    if (stack.length === 1) {
      // const par =
      //     stack[0][0] !== TOKEN_L_PAR || stack[0][stack[0].length - 1] !== TOKEN_R_PAR;
      // serializedFilter = `${par ? TOKEN_L_PAR : ''}${stack[0]}${par ? TOKEN_R_PAR : ''}`;
      serializedFilter = `${stack[0]}`;
    }

    return `${serializedFilter}`;
  }

  static toStringArray(selectedFilters) {
    return clone(selectedFilters ?? [])
      .reverse()
      .map((selFilter) => {
        if (selFilter instanceof Filter) {
          return selFilter.toString();
        }
        // fallback for "manual" filter defined by filterButton
        return `${selFilter.property}:'${selFilter.filter}'`;
      });
  }

  static toMap(selectedFilters, filterSchema) {
    const result = {};
    clone(selectedFilters ?? [])
      .reverse()
      .forEach((selFilter) => {
        if (selFilter instanceof Filter) {
          const rangeProperties = selFilter.getRangeProperties(filterSchema);
          if (rangeProperties?.length === 2) {
            result[rangeProperties[0]] = selFilter.getValueToString({
              rangeProperty: 'START',
            });
            result[rangeProperties[1]] = selFilter.getValueToString({
              rangeProperty: 'END',
            });
          } else {
            result[selFilter.getProperty()] = selFilter.getValueToString();
          }
        } else {
          // fallback for "manual" filter defined by filterButton
          result[selFilter.property] = selFilter.filter;
        }
      });
    return result;
  }
}

export default FilterParser;
